import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
} from "react";
import { Button, CircularProgress, Switch, FormControlLabel } from "@mui/material";
import {
  doc,
  database,
  onSnapshot,
} from "./firebaseConfig";
import { AuthContext } from "../Hooks/AuthProvider";
import {
  formatSyncSummary,
  getActiveSyncProgress,
  hasStaleSync,
} from "./calendarSyncHelpers";

const backfillYear = new Date().getFullYear() - 1;

function ProgressBlock({ label, progress }) {
  if (!progress || progress.status !== "in_progress" || !progress.totalEvents) {
    return null;
  }
  const pct = Math.round((progress.processedEvents / progress.totalEvents) * 100);
  return (
    <div
      style={{
        marginBottom: "12px",
        padding: "12px",
        borderRadius: "4px",
        backgroundColor: "#F0F7FF",
        color: "#096DD9",
        fontSize: "0.9em",
      }}
    >
      {label}: {progress.processedEvents} / {progress.totalEvents} ({pct}%)
      <div
        style={{
          marginTop: "8px",
          height: "4px",
          borderRadius: "2px",
          backgroundColor: "#d6e4ff",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            backgroundColor: "#3498B7",
            transition: "width 0.3s ease",
          }}
        />
      </div>
    </div>
  );
}

const CalendarIntegration = () => {
  const {
    user,
    userSettings,
    revokeAccess,
    refreshUserSettings,
    connectGoogleCalendar,
    syncCalendarNow,
    kickCalendarSync,
    setRentmanSyncEnabled,
    setMotionAirSyncEnabled,
  } = useContext(AuthContext);

  const [isRevoking, setIsRevoking] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [syncingTarget, setSyncingTarget] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusError, setStatusError] = useState(null);
  const [disconnectMessage, setDisconnectMessage] = useState("");

  const isConnected = userSettings?.googleCalendarConnected;
  const motionAirEnabled = userSettings?.motionAirSyncEnabled !== false &&
    userSettings?.hasActiveSync !== false;
  const rentmanEnabled = !!userSettings?.rentmanSyncEnabled;
  const isDisconnecting =
    userSettings?.disconnectProgress?.status === "in_progress";
  const staleSync = hasStaleSync(userSettings);
  const activeSync = getActiveSyncProgress(userSettings);

  const motionAirProgress = userSettings?.motionAirSyncProgress || userSettings?.syncProgress;
  const rentmanProgress = userSettings?.rentmanSyncProgress;

  useEffect(() => {
    if (!user?.email) return;

    const unsubscribe = onSnapshot(
      doc(database, "user-settings", user.email),
      (docSnap) => {
        if (!docSnap.exists()) return;
        const data = docSnap.data();

        const maProgress = data.motionAirSyncProgress || data.syncProgress;
        if (maProgress?.status === "completed") {
          setStatusMessage("Motion Air calendar is up to date.");
          setStatusError(null);
        } else if (maProgress?.status === "error") {
          setStatusError(maProgress.error || "Motion Air sync failed.");
        }

        if (data.rentmanSyncProgress?.status === "completed" && data.rentmanSyncEnabled) {
          setStatusMessage((prev) =>
            prev?.includes("Rentman") ? prev : "Rentman calendar is up to date."
          );
        } else if (data.rentmanSyncProgress?.status === "error") {
          setStatusError(data.rentmanSyncProgress.error || "Rentman sync failed.");
        }
      }
    );

    return () => unsubscribe();
  }, [user?.email]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setStatusError(null);
    setStatusMessage("Connecting… sync will start automatically.");
    try {
      await connectGoogleCalendar();
      setStatusMessage("Connected. Syncing your calendar in the background.");
    } catch (error) {
      setStatusError(error.message || "Failed to connect Google Calendar.");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleRevoke = async () => {
    setIsRevoking(true);
    setStatusError(null);
    setDisconnectMessage("");
    try {
      await revokeAccess();
      setDisconnectMessage(
        "Disconnected. Removing synced events from Google in the background."
      );
      await refreshUserSettings();
    } catch (error) {
      setStatusError(error.message || "Failed to disconnect Google Calendar.");
    } finally {
      setIsRevoking(false);
    }
  };

  const handleSyncNow = useCallback(async (target) => {
    setSyncingTarget(target);
    setStatusError(null);
    try {
      await syncCalendarNow(target);
      setStatusMessage(
        target === "rentman"
          ? "Rentman sync finished."
          : target === "all"
            ? "Calendar sync finished."
            : "Motion Air sync finished."
      );
      await refreshUserSettings();
    } catch (error) {
      setStatusError(error.message || "Sync failed. Try again.");
    } finally {
      setSyncingTarget(null);
    }
  }, [syncCalendarNow, refreshUserSettings]);

  const handleKickSync = useCallback((target) => {
    setStatusError(null);
    setStatusMessage(
      target === "rentman"
        ? "Rentman sync started in the background."
        : "Motion Air sync started in the background."
    );
    kickCalendarSync(target);
  }, [kickCalendarSync]);

  const handleRentmanToggle = async (event) => {
    const enabled = event.target.checked;
    setStatusError(null);
    try {
      await setRentmanSyncEnabled(enabled);
      await refreshUserSettings();
      if (enabled) {
        setStatusMessage("Rentman enabled. Syncing bookings in the background.");
      } else {
        setStatusMessage("Rentman sync disabled.");
      }
    } catch (error) {
      setStatusError(error.message || "Failed to update Rentman sync.");
    }
  };

  const handleMotionAirToggle = async (event) => {
    const enabled = event.target.checked;
    setStatusError(null);
    try {
      await setMotionAirSyncEnabled(enabled);
      await refreshUserSettings();
    } catch (error) {
      setStatusError(error.message || "Failed to update Motion Air sync.");
    }
  };

  return (
    <div>
      <h4 style={{ marginTop: "20px", marginBottom: "8px" }}>
        Google Calendar
      </h4>

      {!isConnected ? (
        <>
          <p style={{ margin: "0 0 16px", color: "#666" }}>
            Connect Google Calendar to sync your Motion Air schedule. You stay
            signed in to the app — this only links your Google account for
            calendar sync.
          </p>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={isConnecting}
            startIcon={
              isConnecting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {isConnecting ? "Connecting…" : "Connect Google Calendar"}
          </Button>
        </>
      ) : (
        <>
          <p style={{ margin: "0 0 16px", color: "#2e7d32" }}>
            Connected to Google Calendar.
          </p>

          {staleSync && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                borderRadius: "6px",
                backgroundColor: "#FFF7E6",
                color: "#AD6800",
                fontSize: "0.9em",
              }}
            >
              A previous sync may have stalled. Use <strong>Sync now</strong> to
              run a fresh sync.
            </div>
          )}

          {activeSync && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                borderRadius: "6px",
                backgroundColor: "#F0F7FF",
                color: "#096DD9",
                fontSize: "0.9em",
              }}
            >
              Syncing {activeSync.label}…{" "}
              {activeSync.progress.totalEvents > 0
                ? `${activeSync.progress.processedEvents} / ${activeSync.progress.totalEvents}`
                : "starting"}
            </div>
          )}

          <div
            style={{
              border: "1px solid #e8e8e8",
              borderRadius: "6px",
              padding: "16px",
              marginBottom: "16px",
            }}
          >
            <strong>Motion Air</strong>
            <p style={{ margin: "8px 0", fontSize: "0.9em", color: "#666" }}>
              Events assigned to you since 1 Jan {backfillYear}. Includes ferie,
              syk, and opptatt.
            </p>
            <FormControlLabel
              control={
                <Switch
                  checked={motionAirEnabled}
                  onChange={handleMotionAirToggle}
                  color="primary"
                />
              }
              label="Sync Motion Air events"
            />
            <ProgressBlock label="Syncing Motion Air" progress={motionAirProgress} />
            {userSettings?.motionAirLastSyncAt && (
              <p style={{ margin: "4px 0 8px", fontSize: "0.85em", color: "#888" }}>
                Last sync: {new Date(userSettings.motionAirLastSyncAt).toLocaleString()}
                {userSettings.motionAirSyncSummary
                  ? ` · ${formatSyncSummary(userSettings.motionAirSyncSummary)}`
                  : ""}
              </p>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleSyncNow("motionAir")}
              disabled={!!syncingTarget}
              sx={{ mt: 1, mr: 1 }}
            >
              {syncingTarget === "motionAir" ? "Syncing…" : "Sync now"}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => handleKickSync("motionAir")}
              disabled={!!syncingTarget}
              sx={{ mt: 1 }}
            >
              Run in background
            </Button>
          </div>

          <div
            style={{
              border: "1px solid #e8e8e8",
              borderRadius: "6px",
              padding: "16px",
              marginBottom: "16px",
            }}
          >
            <strong>Rentman</strong>
            <p style={{ margin: "8px 0", fontSize: "0.9em", color: "#666" }}>
              All visible company bookings as Prep / Shoot / Return in a separate
              <strong> Rentman </strong> calendar. Off by default.
            </p>
            <FormControlLabel
              control={
                <Switch
                  checked={rentmanEnabled}
                  onChange={handleRentmanToggle}
                  color="primary"
                />
              }
              label="Sync Rentman bookings"
            />
            <ProgressBlock label="Syncing Rentman" progress={rentmanProgress} />
            {userSettings?.rentmanLastSyncAt && (
              <p style={{ margin: "4px 0 8px", fontSize: "0.85em", color: "#888" }}>
                Last sync: {new Date(userSettings.rentmanLastSyncAt).toLocaleString()}
                {userSettings.rentmanSyncSummary
                  ? ` · ${formatSyncSummary(userSettings.rentmanSyncSummary)}`
                  : ""}
              </p>
            )}
            {rentmanEnabled && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleSyncNow("rentman")}
                  disabled={!!syncingTarget}
                  sx={{ mt: 1, mr: 1 }}
                >
                  {syncingTarget === "rentman" ? "Syncing…" : "Sync now"}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleKickSync("rentman")}
                  disabled={!!syncingTarget}
                  sx={{ mt: 1 }}
                >
                  Run in background
                </Button>
              </>
            )}
          </div>

          <Button
            variant="outlined"
            color="error"
            onClick={handleRevoke}
            disabled={isRevoking || isDisconnecting}
            startIcon={
              isRevoking ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {isRevoking ? "Disconnecting…" : "Disconnect Google Calendar"}
          </Button>

          {isDisconnecting && (
            <p style={{ margin: "12px 0 0", fontSize: "0.9em", color: "#666" }}>
              Removing synced events from Google…
            </p>
          )}
        </>
      )}

      {(statusMessage || statusError || disconnectMessage) && (
        <div
          style={{
            marginTop: "16px",
            padding: "10px",
            borderRadius: "4px",
            backgroundColor: statusError ? "#FFF3F0" : "#F0F7FF",
            color: statusError ? "#CF1322" : "#096DD9",
            textAlign: "left",
            fontSize: "0.9em",
          }}
        >
          {statusError || disconnectMessage || statusMessage}
        </div>
      )}
    </div>
  );
};

export default CalendarIntegration;
